import React from 'react';
import '../styles/RefundPolicy.css';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const RefundPolicy = () => {
  const lastUpdated = 'MAY 30, 2024';

  return (
    <section id="refund-policy" className="refund-section">
        <Navbar/>
      <div className="refund-container">
        
        {/* Hero Section */}
        <div className="refund-hero">
          <div className="refund-hero-content">
            <h1 className="refund-title">Refund Policy</h1>
            <p className="refund-subtitle">
              Clear, transparent, and customer-friendly refund and return policy.
            </p>
            <div className="refund-last-updated">
              <span>📅 Last Updated: {lastUpdated}</span>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="refund-nav">
          <h3>Quick Navigation</h3>
          <div className="refund-nav-links">
            <a href="#overview">Overview</a>
            <a href="#returns">Return Policy</a>
            <a href="#refunds">Refund Process</a>
            <a href="#damaged">Damaged Products</a>
            <a href="#custom">Custom Branding</a>
            <a href="#exchanges">Exchanges</a>
            <a href="#shipping-returns">Return Shipping</a>
            <a href="#timeline">Refund Timeline</a>
            <a href="#contact-refund">Contact Us</a>
          </div>
        </div>

        <div className="refund-content">
          
          {/* 1. Overview */}
          <div id="overview" className="refund-section-block">
            <h2>1. Overview</h2>
            <div className="refund-underline"></div>
            <p>
              At Preferred Audiology Pvt. Ltd., we are committed to your satisfaction. 
              This Refund Policy outlines the terms and conditions for returns, refunds, 
              and exchanges of our products. Please read this policy carefully before making a purchase.
            </p>
            <p>
              By placing an order with us, you agree to the terms of this Refund Policy. 
              We reserve the right to update or modify this policy at any time without prior notice.
            </p>
          </div>

          {/* 2. Return Policy */}
          <div id="returns" className="refund-section-block">
            <h2>2. Return Policy</h2>
            <div className="refund-underline"></div>
            <p><strong>Eligibility for Returns:</strong></p>
            <ul>
              <li>Products must be returned within <strong>7 days</strong> of delivery</li>
              <li>Products must be in original, unused condition</li>
              <li>All original packaging, accessories, and documentation must be included</li>
              <li>Proof of purchase (order number or invoice) is required</li>
            </ul>
            <p><strong>Non-Returnable Items:</strong></p>
            <ul>
              <li>Custom branded products (unless defective)</li>
              <li>Products damaged due to misuse or mishandling</li>
              <li>Products returned after 7 days</li>
              <li>Products without original packaging</li>
              <li>Sample units (unless defective)</li>
            </ul>
          </div>

          {/* 3. Refund Process */}
          <div id="refunds" className="refund-section-block">
            <h2>3. Refund Process</h2>
            <div className="refund-underline"></div>
            <p><strong>Steps to Request a Refund:</strong></p>
            <ul>
              <li>Contact our customer support within 7 days of delivery</li>
              <li>Provide your order number, product details, and reason for return</li>
              <li>Share clear photos/videos of the product condition</li>
              <li>Wait for approval from our team (within 2-3 business days)</li>
              <li>Ship the product back to our address after approval</li>
              <li>Once received and verified, refund will be processed</li>
            </ul>
            <p><strong>Refund Amount:</strong></p>
            <ul>
              <li>Full refund for defective or damaged products</li>
              <li>Partial refund for products returned in used condition (at our discretion)</li>
              <li>Shipping charges are non-refundable</li>
              <li>Customs duties or taxes (if applicable) are non-refundable</li>
            </ul>
          </div>

          {/* 4. Damaged or Defective Products */}
          <div id="damaged" className="refund-section-block">
            <h2>4. Damaged or Defective Products</h2>
            <div className="refund-underline"></div>
            <p>
              If you receive a damaged or defective product, please contact us immediately:
            </p>
            <ul>
              <li><strong>Time Limit:</strong> Within 48 hours of delivery</li>
              <li><strong>Required Information:</strong> Order number, clear photos/videos of damage</li>
              <li><strong>Resolution Options:</strong>
                <ul>
                  <li>Full refund (including shipping charges)</li>
                  <li>Free replacement product</li>
                  <li>Store credit with additional discount</li>
                </ul>
              </li>
            </ul>
            <div className="refund-alert">
              <span className="refund-alert-icon">⚠️</span>
              <div>
                <strong>Important:</strong> Always inspect your order upon delivery. 
                Do not accept damaged packages from the courier. Report any damage within 48 hours.
              </div>
            </div>
          </div>

          {/* 5. Custom Branding Products */}
          <div id="custom" className="refund-section-block">
            <h2>5. Custom Branding Products (BYOB Program)</h2>
            <div className="refund-underline"></div>
            <p><strong>Special Terms for Custom Orders:</strong></p>
            <ul>
              <li>Custom branded products are non-refundable and non-returnable</li>
              <li>Defective custom products will be replaced with corrected units</li>
              <li>No refunds for design/artwork errors approved by the customer</li>
              <li>Color variations due to printing processes are not considered defects</li>
              <li>Minimum 100 units for custom branding orders</li>
            </ul>
            <p><strong>Defective Custom Products:</strong></p>
            <ul>
              <li>Replacement will be provided at no additional cost</li>
              <li>Replacement timeline: 15-20 days</li>
              <li>Quality issues must be reported within 7 days of delivery</li>
            </ul>
          </div>

          {/* 6. Exchanges */}
          <div id="exchanges" className="refund-section-block">
            <h2>6. Exchanges</h2>
            <div className="refund-underline"></div>
            <p>
              We only replace products if they are defective or damaged. If you need to exchange a product 
              for a different model, please contact our support team.
            </p>
            <p><strong>Exchange Process:</strong></p>
            <ul>
              <li>Return the original product as per return policy</li>
              <li>Place a new order for the desired product</li>
              <li>Price difference will be adjusted accordingly</li>
              <li>Shipping charges for exchange will be borne by the customer</li>
            </ul>
          </div>

          {/* 7. Return Shipping */}
          <div id="shipping-returns" className="refund-section-block">
            <h2>7. Return Shipping</h2>
            <div className="refund-underline"></div>
            <p><strong>Who Pays for Return Shipping?</strong></p>
            <ul>
              <li><strong>Defective/Damaged Products:</strong> We cover return shipping costs</li>
              <li><strong>Change of Mind:</strong> Customer bears return shipping costs</li>
              <li><strong>Wrong Product Shipped:</strong> We cover return shipping costs</li>
            </ul>
            <p><strong>Return Address:</strong></p>
            <div className="refund-address">
              <p>Preferred Audiology Pvt. Ltd.<br />
              Ecospace Business Towers, Tower 5A, Office no. 508<br />
              New Town, Kolkata - 700160<br />
              West Bengal, India</p>
            </div>
            <p>
              <strong>Note:</strong> We recommend using a trackable shipping service or purchasing 
              shipping insurance for returns. We are not responsible for lost return packages.
            </p>
          </div>

          {/* 8. Refund Timeline */}
          <div id="timeline" className="refund-section-block">
            <h2>8. Refund Timeline</h2>
            <div className="refund-underline"></div>
            <p><strong>Processing Time:</strong></p>
            <ul>
              <li><strong>Inspection:</strong> 2-3 business days after receiving the return</li>
              <li><strong>Approval:</strong> 1-2 business days after inspection</li>
              <li><strong>Refund Processing:</strong> 5-7 business days after approval</li>
            </ul>
            <p><strong>Refund Method:</strong></p>
            <ul>
              <li><strong>Credit Card/Debit Card:</strong> 5-10 business days</li>
              <li><strong>Bank Transfer (NEFT/IMPS):</strong> 3-5 business days</li>
              <li><strong>UPI/Net Banking:</strong> 3-5 business days</li>
              <li><strong>Store Credit:</strong> Instant upon approval</li>
            </ul>
            <div className="refund-note">
              <span>ℹ️</span>
              <p>
                If you haven't received your refund after the mentioned timeline, please check with 
                your bank or payment provider first. Then contact our support team for assistance.
              </p>
            </div>
          </div>

          {/* 9. Late or Missing Refunds */}
          <div className="refund-section-block">
            <h2>9. Late or Missing Refunds</h2>
            <div className="refund-underline"></div>
            <p>If you haven't received a refund yet:</p>
            <ul>
              <li>Check your bank account again (refunds may take time to appear)</li>
              <li>Contact your credit card company or bank (processing times vary)</li>
              <li>Check with your payment provider (UPI, NetBanking, etc.)</li>
              <li>If you've done all of the above and still haven't received your refund, please contact us.</li>
            </ul>
          </div>

          {/* 10. Sale Items */}
          <div className="refund-section-block">
            <h2>10. Sale Items</h2>
            <div className="refund-underline"></div>
            <p>
              Only regular-priced items may be refunded. Sale items or discounted products are 
              generally non-refundable unless they are defective or damaged.
            </p>
          </div>

          {/* 11. Bulk Orders */}
          <div className="refund-section-block">
            <h2>11. Bulk Orders</h2>
            <div className="refund-underline"></div>
            <p><strong>Special Terms for Bulk Orders (50+ units):</strong></p>
            <ul>
              <li>Defective units will be replaced, not refunded</li>
              <li>Return window: 14 days for bulk orders</li>
              <li>Sample testing available before bulk purchase</li>
              <li>Quality issues must be reported within 7 days</li>
            </ul>
          </div>

          {/* 12. Instant Service & Replacement */}
          <div className="refund-section-block">
            <h2>12. Instant Service & Replacement</h2>
            <div className="refund-underline"></div>
            <p>
              Under our unique "Instant Service & Replacement" program, partner clinics can replace 
              hearing aids instantly for end customers. Terms of this program are governed by the 
              partnership agreement.
            </p>
            <ul>
              <li>Clinics receive replacement units immediately</li>
              <li>Defective units are replaced, not refunded</li>
              <li>Program available for enrolled partner clinics only</li>
            </ul>
          </div>

          {/* 13. Cancellation Policy */}
          <div className="refund-section-block">
            <h2>13. Cancellation Policy</h2>
            <div className="refund-underline"></div>
            <p><strong>Order Cancellation:</strong></p>
            <ul>
              <li><strong>Within 24 hours of order:</strong> Full refund</li>
              <li><strong>After 24 hours, before shipping:</strong> 10% cancellation fee</li>
              <li><strong>After order is shipped:</strong> No cancellation allowed</li>
              <li><strong>Custom branding orders:</strong> No cancellation after artwork approval</li>
            </ul>
          </div>

          {/* 14. Contact Information */}
          <div id="contact-refund" className="refund-section-block">
            <h2>14. Contact Us</h2>
            <div className="refund-underline"></div>
            <p>If you have any questions about this Refund Policy, please contact us:</p>
            <div className="refund-contact-info">
              <div className="refund-contact-item">
                <span className="refund-contact-icon">📞</span>
                <div>
                  <strong>Phone</strong>
                  <a href="tel:03323240508">033-23240508</a>
                </div>
              </div>
              <div className="refund-contact-item">
                <span className="refund-contact-icon">💬</span>
                <div>
                  <strong>WhatsApp</strong>
                  <a href="https://wa.me/919123944757">+91 9123944757</a>
                </div>
              </div>
              <div className="refund-contact-item">
                <span className="refund-contact-icon">✉️</span>
                <div>
                  <strong>Email</strong>
                  <a href="mailto:returns@paplhear.com">returns@paplhear.com</a>
                </div>
              </div>
              <div className="refund-contact-item">
                <span className="refund-contact-icon">📍</span>
                <div>
                  <strong>Address</strong>
                  <p>Preferred Audiology Pvt. Ltd.<br />
                  Ecospace Business Towers, Tower 5A, Office no. 508<br />
                  New Town, Kolkata - 700160</p>
                </div>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <div className="refund-download">
            <button className="refund-download-btn">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 16L4 18C4 19.1046 4.89543 20 6 20L18 20C19.1046 20 20 19.1046 20 18L20 16M12 4L12 15M12 15L15 12M12 15L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Download Refund Policy (PDF)
            </button>
          </div>

        </div>
      </div>
      <Footer/>
    </section>
  );
};

export default RefundPolicy;